import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBrand } from '../context/BrandContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { API_BASE_URL as API } from '../utils/apiConfig';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2 } from 'lucide-react';

const FUNDLE_LOGO = 'https://customer-assets.emergentagent.com/job_fundle-ai/artifacts/tu7vfzht_erj2rwul_fundle_logo-white-min-382x100.png';

export default function EmailLogin() {
  const navigate = useNavigate();
  const { login, isAuthenticated, checkAuth } = useAuth();
  const { brand, subdomain, isSuperAdmin } = useBrand();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);

  useEffect(() => {
    const check = async () => {
      const user = await checkAuth();
      if (user) {
        navigate('/dashboard');
      }
      setCheckingAuth(false);
    };
    check();
  }, [checkAuth, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Please enter email and password');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(`${API}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email: email.toLowerCase().trim(),
          password,
          subdomain: subdomain || 'fundlebrain'
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Login failed');
      }
      
      // Store user brand info for brand admins
      if (data.user.brand_id) {
        localStorage.setItem('userBrandId', data.user.brand_id);
        localStorage.setItem('selectedBrandId', data.user.brand_id);
      }
      localStorage.setItem('isSuperAdmin', data.user.is_super_admin ? 'true' : 'false');
      
      // Update auth context
      login(data.user);
      
      toast.success(`Welcome back, ${data.user.name}!`);
      navigate('/dashboard');
      
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.message || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  if (checkingAuth || isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Get brand logo and colors
  const brandLogo = brand?.logo_url || FUNDLE_LOGO;
  const brandName = brand?.name || 'Fundle Brain';
  const primaryColor = brand?.theme?.primary_color || '#8B5CF6';

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="email-login-page">
      {/* Background gradient */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(ellipse at top right, ${primaryColor}15, transparent 50%)`
        }}
      />
      
      {/* Main content */}
      <div className="relative flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md space-y-8">
          {/* Logo */}
          <div className="text-center">
            <img 
              src={brandLogo} 
              alt={brandName}
              className="h-12 mx-auto mb-8 object-contain"
              data-testid="brand-logo"
            />
            <h1 className="text-3xl font-bold tracking-tight mb-3">
              {isSuperAdmin ? 'Super Admin Portal' : `${brandName} Analytics`}
            </h1>
            <p className="text-muted-foreground">
              Sign in to access your loyalty analytics dashboard
            </p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  data-testid="email-input"
                  className="h-12"
                  autoComplete="email"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                    data-testid="password-input"
                    className="h-12 pr-12"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-12 text-base"
              disabled={loading}
              data-testid="login-submit-btn"
              style={{ backgroundColor: primaryColor }}
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          {/* Alternative login option */}
          {!subdomain || subdomain === 'fundlebrain' ? (
            <div className="text-center">
              <p className="text-sm text-muted-foreground mb-4">Or continue with</p>
              <Link to="/login">
                <Button variant="outline" className="w-full h-12 gap-3" data-testid="google-login-link">
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                  Continue with Google
                </Button>
              </Link>
            </div>
          ) : null}

          {/* Footer */}
          <div className="text-center pt-4 border-t border-border/50">
            <p className="text-xs text-muted-foreground">
              Powered by{' '}
              <a href="https://fundle.ai" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                Fundle
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
